import { useMediaQuery } from "@mui/material";
import { FC, useContext, useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    ArrowDownIcon,
    ArrowUpIcon,
    InfoIcon,
    StarIcon,
    StarOutlinedIcon,
} from "../../../assets/img/icons";
import { Select } from "../../../components/OldSelect";
import ActionSelect from "../../../components/Select";
import Table from "../../../components/Table";
import { AuthContext } from "../../../contexts/AuthContext";
import { AdminMembersSortBy } from "../../../types/api/admin/user";
import { cacheMemberDetailsUser } from "../../../services/api/admin/user";
import { updateUserRole } from "../../../services/api/admin/role";
import { UserAdminResult } from "../../../types/User";

import clsx from "clsx";
import ConfirmModal from "../../../components/ConfirmModal";
import IconButton from "../../../components/IconButton";
import OpenInNewTabLink from "../../../components/OpenInNewTabLink";

import Stat from "../../../components/Text/Stat";
import { getUserName } from "../../../utils/string";
import { isStaffRole } from "../../../utils/graduation";
import { Role } from "../../../types/enums/Role";
import PromoteToStaffModal from "../PromoteToStaffModal";
import styles from "./styles.module.scss";
import SelectionBulkModal, {
    SelectionBulkMode,
} from "./SelectionBulkModal";

/** Taille des chiffres (Stat / séances) — 16px, aligné avec `.stat_cell_text` */
const MEMBERS_TABLE_STAT_STYLE: CSSProperties = {
    fontSize: 16,
    lineHeight: "20.08px",
};

const formatSessionsPeriod = (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    if (Number.isNaN(n)) return null;
    return new Intl.NumberFormat("fr-FR").format(n);
};

const renderCurrencyStat = (
    value: number | null | undefined,
    inactiveCls: string
) => {
    if (value === null || value === undefined) {
        return (
            <span className={clsx(styles.label_disabled, inactiveCls)}>—</span>
        );
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
        return (
            <span className={clsx(styles.label_disabled, inactiveCls)}>—</span>
        );
    }
    return (
        <Stat
            type="currency"
            variant="body-bold"
            className={inactiveCls}
            style={MEMBERS_TABLE_STAT_STYLE}
        >
            {n}
        </Stat>
    );
};

const renderPercentStat = (
    value: number | null | undefined,
    decimals: number,
    inactiveCls: string
) => {
    if (value === null || value === undefined) {
        return (
            <span className={clsx(styles.label_disabled, inactiveCls)}>—</span>
        );
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
        return (
            <span className={clsx(styles.label_disabled, inactiveCls)}>—</span>
        );
    }
    return (
        <Stat
            type="percentage"
            decimals={decimals}
            variant="body-bold"
            className={inactiveCls}
            style={MEMBERS_TABLE_STAT_STYLE}
        >
            {n}
        </Stat>
    );
};

interface Props {
    isLoading: boolean;
    users: UserAdminResult[];
    roles: { id: string; name: string }[];
    page: number;
    userByPage: number;
    onRoleChange: (userId: string, roleId: string) => void;
    handleChangeOrder: (by: AdminMembersSortBy) => void;
    handleOpenModalEditUser: (user: UserAdminResult) => void;
    handleConfirmDeleteUser: (user: UserAdminResult) => void;
    handleViewUser: (user: UserAdminResult) => void;
    sort: {
        by: AdminMembersSortBy;
        order: "asc" | "desc";
    };
    onToggleFavorite: (isCurrentlyFavorite: boolean, userId: string) => void;
    onBulkCompleted?: () => void;
}

const MembersTable: FC<Props> = ({
    isLoading,
    users,
    roles,
    page,
    userByPage,
    onRoleChange,
    handleChangeOrder,
    handleOpenModalEditUser,
    handleConfirmDeleteUser,
    handleViewUser,
    sort,
    onToggleFavorite,
    onBulkCompleted,
}) => {
    const navigate = useNavigate();
    const {
        hasPermission,
        isStreamOn,
        user: connectedUser,
    } = useContext(AuthContext)!;
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const [isExiting, setIsExiting] = useState(false);
    const [hasEverSelected, setHasEverSelected] = useState(false);

    const [bulkMode, setBulkMode] = useState<SelectionBulkMode | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedAction, setSelectedAction] = useState("");
    const [staffPromoteUser, setStaffPromoteUser] =
        useState<UserAdminResult | null>(null);
    const [formeReturnUser, setFormeReturnUser] =
        useState<UserAdminResult | null>(null);
    const [formeReturnLoading, setFormeReturnLoading] = useState(false);
    const canBulkAdmin = hasPermission("admin-members-bulk", "update");
    const canAssignPromotion = hasPermission("admin-promotions", "update");
    const traderRoles = roles.filter((role) => !isStaffRole(role.name));

    const bulkActionOptions = [
        { value: "add_tag", label: "Ajouter un tag" },
        { value: "remove_tag", label: "Retirer un tag" },
        ...(canAssignPromotion
            ? [
                  {
                      value: "assign_promotion",
                      label: "Attribuer à une promotion",
                  },
              ]
            : []),
        ...(canBulkAdmin
            ? [
                  { value: "change_role", label: "Changer le rôle" },
                  { value: "deactivate", label: "Désactiver" },
              ]
            : []),
    ];

    useEffect(() => {
        if (selectedUserIds.length > 0) {
            setHasEverSelected(true);
            setIsExiting(false);
        } else if (
            selectedUserIds.length === 0 &&
            hasEverSelected &&
            !isExiting
        ) {
            // Trigger exit animation only if user has previously selected items
            setIsExiting(true);
            setSelectedAction("");
        }
    }, [selectedUserIds.length, hasEverSelected, isExiting]);

    const handleOpenBulk = (mode: SelectionBulkMode) => {
        setBulkMode(mode);
    };

    const handleActionChange = (selected: string | string[]) => {
        const value = typeof selected === "string" ? selected : "";
        setSelectedAction(value);
        if (
            value === "add_tag" ||
            value === "remove_tag" ||
            value === "change_role" ||
            value === "deactivate" ||
            value === "assign_promotion"
        ) {
            handleOpenBulk(value);
        }
    };

    const handleSubmitBulk = () => {
        setBulkMode(null);
        setSelectedAction("");
        onBulkCompleted?.();

        // Trigger exit animation
        setIsExiting(true);

        // Wait for animation to complete before clearing selection
        setTimeout(() => {
            setSelectedUserIds([]);
            setIsExiting(false);
            setHasEverSelected(false);
        }, 300); // Match animation duration
    };

    const handleCloseBulk = () => {
        setBulkMode(null);
        setSelectedAction("");
    };

    const handleConfirmReturnToForme = async () => {
        if (!formeReturnUser) return;
        setFormeReturnLoading(true);
        try {
            await updateUserRole(formeReturnUser.id, Role.Forme);
            toast.success("Le compte est repassé en Formé.");
            setFormeReturnUser(null);
            onBulkCompleted?.();
        } catch {
            toast.error("Échec du retour en Formé");
        } finally {
            setFormeReturnLoading(false);
        }
    };

    const getOptions = (user: UserAdminResult) => {
        let options: { label: string; onClick: () => void }[] = [];

        if (hasPermission("admin-trading-sessions", "read")) {
            options = options.concat([
                {
                    label: "Voir les trades",
                    onClick: () => handleSeeTrades(user),
                },
            ]);
        }
        if (
            hasPermission("admin-graduation", "read") ||
            hasPermission("admin-formateur-graduation", "read")
        ) {
            options = options.concat([
                {
                    label: "Voir le suivi",
                    onClick: () => handleSeeFormation(user),
                },
            ]);
        }

        if (hasPermission("admin-members", "update")) {
            const canChangeStaffRole =
                user.role !== Role.SuperAdmin ||
                connectedUser?.role === Role.SuperAdmin;
            options = options.concat([
                {
                    label: "Editer",
                    onClick: () => handleOpenModalEditUser(user),
                },
                ...(canChangeStaffRole
                    ? [
                          {
                              label: "Promouvoir en staff",
                              onClick: () => setStaffPromoteUser(user),
                          },
                          ...(isStaffRole(user.role)
                              ? [
                                    {
                                        label: "Repasser en Formé",
                                        onClick: () =>
                                            setFormeReturnUser(user),
                                    },
                                ]
                              : []),
                      ]
                    : []),
                {
                    label: `${user.isActive ? "Désa" : "A"}ctiver le compte`,
                    onClick: () => handleConfirmDeleteUser(user),
                },
            ]);
        }

        return options;
    };

    const handleSeeTrades = (user: UserAdminResult) => {
        cacheMemberDetailsUser(user);
        // Stocker l'URL actuelle avec tous les filtres pour pouvoir y revenir
        const currentUrl = window.location.pathname + window.location.search;
        sessionStorage.setItem("returnToSearchUrl", currentUrl);
        // Snapshot pagination (page + résultats par page) au clic,
        // pour éviter que le retour ne tombe sur la page 1.
        sessionStorage.setItem(
            "adminMembersReturnPagination",
            JSON.stringify({ page, userByPage })
        );
        navigate(`/trades?userId=${user.id}`);
    };

    const handleSeeFormation = (user: UserAdminResult) => {
        cacheMemberDetailsUser(user);
        const currentUrl = window.location.pathname + window.location.search;
        sessionStorage.setItem("returnToSearchUrl", currentUrl);
        sessionStorage.setItem(
            "adminMembersReturnPagination",
            JSON.stringify({ page, userByPage })
        );
        navigate(`/formation?userId=${user.id}`);
    };

    const iconFilter = (column: string) => {
        if (column === sort.by) {
            const Icon = sort.order === "asc" ? ArrowUpIcon : ArrowDownIcon;
            return <Icon width={12} />;
        }

        return <></>;
    };

    const formatUsers = (users: UserAdminResult[]) =>
        users.map((user, key) => {
            const promotionLabel = user.promotionName?.trim() || null;

            let values = {};

            if (isMobile) {
                values = {
                    performance_total: (
                        <div
                            className={
                                styles.admin_columns_performance_container
                            }
                        >
                            <Stat
                                type="currency"
                                variant="body-bold"
                                className={
                                    !user.isActive ? styles.label_disabled : ""
                                }
                                style={MEMBERS_TABLE_STAT_STYLE}
                            >
                                {user.performance_total}
                            </Stat>
                            <Stat
                                type="percentage"
                                variant="body-bold"
                                className={
                                    !user.isActive ? styles.label_disabled : ""
                                }
                                style={MEMBERS_TABLE_STAT_STYLE}
                            >
                                {user.performance_percentage}
                            </Stat>
                        </div>
                    ),
                };
            } else {
                values = {
                    performance_total: (
                        <div
                            className={
                                !user.isActive
                                    ? styles.perf_cell_disabled
                                    : styles.perf_cell
                            }
                        >
                            <Stat
                                type="currency"
                                variant="body-bold"
                                style={MEMBERS_TABLE_STAT_STYLE}
                            >
                                {user.performance_total}
                            </Stat>
                            <div className={styles.perf_meta}>
                                {user.performance_total >= 0 ? (
                                    <ArrowUpIcon
                                        width={10}
                                        className={styles.perf_meta_gain}
                                    />
                                ) : (
                                    <ArrowDownIcon
                                        width={10}
                                        className={styles.perf_meta_loss}
                                    />
                                )}
                                <span className={styles.perf_meta_text}>
                                    {user.performance_percentage} %
                                </span>
                            </div>
                        </div>
                    ),
                    promotion: promotionLabel ? (
                        <span
                            className={
                                !user.isActive ? styles.label_disabled : ""
                            }
                        >
                            {promotionLabel}
                        </span>
                    ) : (
                        <span className={styles.label_disabled}>—</span>
                    ),
                };
            }

            const inactiveCls = !user.isActive ? styles.label_disabled : "";

            return {
                ...user,
                ...values,
                sessions_count: (
                    <span className={clsx(styles.stat_cell_text, inactiveCls)}>
                        {formatSessionsPeriod(user.sessions_count) ?? "—"}
                    </span>
                ),
                average_gain: renderCurrencyStat(user.average_gain, inactiveCls),
                average_loss: renderCurrencyStat(user.average_loss, inactiveCls),
                day_win_percentage: renderPercentStat(
                    user.day_win_percentage,
                    1,
                    inactiveCls
                ),
                method_respect_percentage: renderPercentStat(
                    user.method_respect_percentage,
                    1,
                    inactiveCls
                ),
                infos: (
                    <div
                        className={clsx(
                            styles.infosIcon,
                            !user.isActive && styles.label_disabled
                        )}
                    >
                        {/* <Tooltip
                            placement="right"
                            content={user.notes[0]?.content || ""}
                        > */}
                        <IconButton
                            size="medium"
                            icon={InfoIcon}
                            onClick={() => handleViewUser(user)}
                        />
                        {/* </Tooltip> */}
                    </div>
                ),
                pseudo: (
                    <span
                        className={clsx(
                            styles.name_value,
                            !user.isActive && styles.label_disabled
                        )}
                    >
                        <span onClick={() => handleSeeTrades(user)}>
                            {getUserName(
                                user.firstname,
                                user.lastname,
                                user.pseudo,
                                isStreamOn
                            )}
                        </span>
                        <OpenInNewTabLink
                            href={`/trades?userId=${encodeURIComponent(user.id)}`}
                        />
                    </span>
                ),
                role:
                    roles.length > 0 &&
                    hasPermission("admin-members", "update") ? (
                        isStaffRole(user.role) ? (
                            <span
                                className={clsx(
                                    !user.isActive && styles.label_disabled
                                )}
                            >
                                {user.role}
                            </span>
                        ) : (
                            <div className={styles.admin_columns_role}>
                                <Select
                                    disabled={!user.isActive}
                                    className={styles.admin_columns_role_select}
                                    onChanged={(value) =>
                                        onRoleChange(user.id, value as string)
                                    }
                                    value={user.roleId}
                                >
                                    {traderRoles.map((role) => (
                                        <option key={role.id} value={role.id}>
                                            {role.name}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        )
                    ) : (
                        <span>{user.role}</span>
                    ),
                isFavorite:
                    user.id === connectedUser?.id ? (
                        <></>
                    ) : (
                        <IconButton
                            icon={user.isFavorite ? StarIcon : StarOutlinedIcon}
                            onClick={() =>
                                onToggleFavorite(user.isFavorite, user.id)
                            }
                        />
                    ),
                options: getOptions(user),
            };
        });

    const getRows = () => {
        let keys = [
            {
                label: "",
                value: "infos",
                width: 36,
            },
            {
                label: (
                    <div className={styles.name_key}>
                        NOM <span>{iconFilter("pseudo")}</span>
                    </div>
                ),
                value: "pseudo",
                onClick: () => handleChangeOrder("pseudo"),
            },
            { label: "RÔLE", value: "role", width: 120 },
            {
                label: (
                    <div style={{ cursor: "pointer" }}>
                        MÉTHODE (%) {iconFilter("method")}
                    </div>
                ),
                value: "method_respect_percentage",
                onClick: () => handleChangeOrder("method"),
            },
            {
                label: (
                    <div style={{ cursor: "pointer" }}>
                        PERFORMANCES {iconFilter("performance")}
                    </div>
                ),
                value: "performance_total",
                onClick: () => handleChangeOrder("performance"),
            },
            {
                label: (
                    <div style={{ cursor: "pointer" }}>
                        GAIN MOYEN / TRADE {iconFilter("average_gain")}
                    </div>
                ),
                value: "average_gain",
                onClick: () => handleChangeOrder("average_gain"),
            },
            {
                label: (
                    <div style={{ cursor: "pointer" }}>
                        PERTE MOYENNE / TRADE {iconFilter("average_loss")}
                    </div>
                ),
                value: "average_loss",
                onClick: () => handleChangeOrder("average_loss"),
            },
            {
                label: <div>DAY WIN (%)</div>,
                value: "day_win_percentage",
            },
            {
                label: <div>NB DE SÉANCES</div>,
                value: "sessions_count",
            },
            {
                label: "PROMOTION",
                value: "promotion",
            },
            {
                label: "",
                value: "isFavorite",
                width: 36,
            },
        ];

        return isMobile
            ? keys.filter((k) =>
                  ["infos", "pseudo", "isFavorite"].includes(k.value)
              )
            : keys;
    };

    return (
        <>
            <SelectionBulkModal
                isOpen={bulkMode !== null}
                mode={bulkMode}
                selectedUserIds={selectedUserIds}
                onClose={handleCloseBulk}
                onSubmit={handleSubmitBulk}
            />
            <PromoteToStaffModal
                open={Boolean(staffPromoteUser)}
                handleClose={() => setStaffPromoteUser(null)}
                userId={staffPromoteUser?.id ?? null}
                userDisplayName={
                    staffPromoteUser
                        ? getUserName(
                              staffPromoteUser.firstname,
                              staffPromoteUser.lastname,
                              staffPromoteUser.pseudo,
                              isStreamOn
                          )
                        : undefined
                }
                currentRoleName={staffPromoteUser?.role}
                roles={roles}
                actorRoleName={connectedUser?.role}
                onSuccess={onBulkCompleted}
            />
            <ConfirmModal
                open={formeReturnUser !== null}
                handleClose={() => {
                    if (!formeReturnLoading) setFormeReturnUser(null);
                }}
                handleConfirm={() => {
                    void handleConfirmReturnToForme();
                }}
                isLoading={formeReturnLoading}
                title="Repasser en Formé ?"
                confirmLabel="Repasser en Formé"
                description={
                    formeReturnUser
                        ? `${getUserName(
                              formeReturnUser.firstname,
                              formeReturnUser.lastname,
                              formeReturnUser.pseudo,
                              isStreamOn
                          )} quitte le rôle ${formeReturnUser.role} et redevient Formé.`
                        : undefined
                }
            />
            <Table
                loading={isLoading}
                tableLayout={"fixed"}
                rows={getRows()}
                data={formatUsers(users)}
                noDataLabel="Aucun membre trouvé"
                selectable={true}
                selectedIds={selectedUserIds}
                onSelectionChange={setSelectedUserIds}
            />
            {(selectedUserIds.length > 0 || isExiting) && (
                <div
                    className={clsx(
                        styles.action_bar,
                        isExiting && styles.action_bar_exit
                    )}
                >
                    <div
                        className={styles.selection_meta}
                        aria-label={`${selectedUserIds.length} sélectionné${
                            selectedUserIds.length > 1 ? "s" : ""
                        }`}
                    >
                        <span className={styles.selection_count_badge}>
                            {selectedUserIds.length}
                        </span>
                        <span className={styles.selection_count_label}>
                            sélectionné
                            {selectedUserIds.length > 1 ? "s" : ""}
                        </span>
                    </div>
                    <div className={styles.action_bar_actions}>
                        <ActionSelect
                            options={bulkActionOptions}
                            value={selectedAction}
                            onChange={handleActionChange}
                            placeholder="Que faire ?"
                            classNameTrigger={styles.action_bar_select}
                            placement="top-end"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default MembersTable;
